import { z } from 'zod';

/**
 * PowerSchool · Timetable class list (owner 2026-10-26, DECISIONS "Timetable class list
 * panel"): the students of one of the teacher's own sections, from PowerSchool's built-in
 * PowerQuery `com.pearson.core.teachers.sectionEnrollments`. Names pass through tenant-api and
 * are never stored there or on the device. Gender and date of birth are dropped at parse time.
 */

export const POWERSCHOOL_SECTION_STUDENTS_PATH =
  '/api/v1/powerschool/timetable/sections/:sectionId/students';

/** A section id as the week response gives it (`TimetableSection.sectionId`): plain characters. */
export const timetableSectionIdSchema = z.string().regex(/^[A-Za-z0-9-]{1,40}$/);

/** The concrete path for one section (throws on an id that isn't plain). */
export function powerSchoolSectionStudentsPath(sectionId: string): string {
  const id = timetableSectionIdSchema.parse(sectionId);
  return POWERSCHOOL_SECTION_STUDENTS_PATH.replace(':sectionId', id);
}

/** PowerQuery leaves out null columns, and drops columns the plugin may not read: '' then. */
const vendorString = z.string().default('');

/**
 * One row of `com.pearson.core.teachers.sectionEnrollments`. Only the columns TeachPartner uses
 * are kept; `gender` and `dob` (also in the query) are stripped here and never go further.
 */
export const powerSchoolEnrollmentRecordSchema = z.object({
  dcid: vendorString,
  id: vendorString,
  student_number: vendorString,
  first_name: vendorString,
  last_name: vendorString,
  lastfirst: vendorString,
  grade_level: vendorString,
});
export type PowerSchoolEnrollmentRecord = z.infer<typeof powerSchoolEnrollmentRecordSchema>;

export const powerSchoolEnrollmentsResponseSchema = z.object({
  count: z.coerce.number().int().min(0).optional(),
  record: z.array(powerSchoolEnrollmentRecordSchema).default([]),
});
export type PowerSchoolEnrollmentsResponse = z.infer<typeof powerSchoolEnrollmentsResponseSchema>;

/** One student as the app gets it. Strict: a gender (or any other extra field) is refused. */
export const timetableStudentSchema = z.strictObject({
  /** PowerSchool students.dcid. */
  id: z.string().min(1),
  studentNumber: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  gradeLevel: z.string(),
});
export type TimetableStudent = z.infer<typeof timetableStudentSchema>;

/**
 * GET /api/v1/powerschool/timetable/sections/:sectionId/students. Only for a section in the
 * effective teacher's own `teacher_sections` (404 otherwise). Sorted by last, then first name.
 */
export const powerSchoolSectionStudentsResponseSchema = z.object({
  sectionId: timetableSectionIdSchema,
  students: z.array(timetableStudentSchema),
  fetchedAt: z.iso.datetime(),
  /** Set when the class is another teacher's (developer view-as). */
  viewingAs: z.email().optional(),
});
export type PowerSchoolSectionStudentsResponse = z.infer<
  typeof powerSchoolSectionStudentsResponseSchema
>;
